import { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import { LazyLoadRowProps } from "../shared-components/LazyLoadTable";
import { ReportRow } from "./Table";
import ReportStatus from "./ReportStatus";
import ReportStepsModal from "./ReportStepsModal";
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import MisconductCancelButton from "./MisconductCancelButton";
import { useReportByUUID } from "../api/ReportsApi";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import useNavigateWithState from "./useNavigateWithState";
import { WorkflowRunEx } from "../api/Workflows";
import { ReportSummaryItemApi } from "../api/Reports";
import { reportFailed } from "./utils";
import { formattedDate } from "../utils";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import NotLoadedYet from "../shared-components/NotLoadedYet";
import {
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";

const ClientQALazyRow = ({
  parentRow,
  index,
}: LazyLoadRowProps<WorkflowRunEx>): JSX.Element => {
  const { navigateWithState } = useNavigateWithState();
  const { mutate: reportGet, data: report } = useReportByUUID();
  const permissions = usePermissionValidator();
  const [row, setRow] = useState<ReportRowItem<ReportSummaryItemApi> | null>(
    null
  );

  useEffect(() => {
    const key = parentRow.payload.report_uuid;
    if (key) {
      reportGet(key as string);
    } else {
      setRow(null);
    }
  }, [parentRow, reportGet]);

  useEffect(() => {
    if (!report) {
      setRow(WorkflowRunExToRow(parentRow));
    } else {
      setRow(ReportSummaryItemApiToRow(report));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentRow, report, setRow]);

  function WorkflowRunExToRow(
    wrx: WorkflowRunEx
  ): ReportRowItem<ReportSummaryItemApi> {
    const rowItem = {
      report_uuid: wrx.payload.report_uuid,
      report: undefined,
      isLocked: undefined,
      data: [
        {
          text: wrx.payload.full_name || "?",
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div className="flex">
              <div>
                <span className="font-semibold">
                  {wrx.payload.full_name || "?"}
                </span>
                <small className="block font-normal">
                  {wrx.payload.company_name || "?"}
                </small>
              </div>
            </div>
          ),
        },
        {
          text: "status",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
        {
          text: "check type",
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div>
              <NotLoadedYet className="h-4 w-12 block" />
              <br />
              <NotLoadedYet className="h-4 w-16 block" />
            </div>
          ),
        },
        {
          text: "date added",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
        {
          text: "due date",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
        {
          text: "request uuid",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet className="h-6 w-20" />,
        },
        {
          text: "custom id",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
      ],
    };
    if (permissions.canCancelMisconduct) {
      rowItem.data.push({
        text: "cancel",
        style: ReportEntryStyle.COMPONENT,
        component: (
          <MisconductCancelButton reportUUID={wrx.payload.report_uuid} />
        ),
      });
    }
    return rowItem;
  }

  function ReportSummaryItemApiToRow(
    rsi: ReportSummaryItemApi
  ): ReportRowItem<ReportSummaryItemApi> {
    const rowItem: ReportRowItem<ReportSummaryItemApi> = {
      report_uuid: rsi.report_uuid,
      report: rsi,
      isLocked: rsi.is_locked,
      data: [
        {
          text: rsi.full_name,
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div className="flex">
              <div>
                {permissions.canClientQaEdit ? (
                  <span
                    onClick={() => navigateWithState(rsi.report_uuid, "cqa")}
                    className="font-semibold underline cursor-pointer"
                  >
                    {rsi.full_name}
                  </span>
                ) : (
                  <span className="font-semibold">{rsi.full_name}</span>
                )}
                <small className="block font-normal">{rsi.company_name}</small>
              </div>
              <div>
                {(rsi.step_payload?.rerun_from_parent_uuid ||
                  rsi.has_rerun) && (
                  <div>
                    <ArrowPathIcon className="w-5 ml-2 text-blue-600" />
                  </div>
                )}
              </div>
            </div>
          ),
        },
        {
          text: rsi.report_status,
          style: ReportEntryStyle.COMPONENT,
          component: (
            <>
              <ReportStatus
                stepStatusName={rsi.step_name}
                failed={reportFailed(rsi)}
                report={rsi}
                allowResumeOnClick={true}
              />
              <ReportStepsModal workflowRunUUID={rsi.workflow_run_uuid} />
            </>
          ),
          customClass: "flex-1 w-4",
        },
        {
          text: rsi.blueprint_name + " " + (rsi.behavior_kit_name ?? "General"),
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div className="flex flex-col">
              <span className="border-b-2">{rsi.blueprint_name}</span>{" "}
              <small className="block">
                {rsi.behavior_kit_name ?? "General"}
              </small>
            </div>
          ),
        },
        {
          text: formattedDate(rsi.report_date_added),
          style: ReportEntryStyle.COMPONENT,
          customClass: "flex-1 whitespace-pre",
          component: rsi.recur ? (
            <>
              <span>{formattedDate(rsi.report_date_added)}</span>
              <span data-tip="Employee Screening" className="cursor-pointer">
                <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
              </span>
              <ReactTooltip effect="solid" />
            </>
          ) : (
            <>{formattedDate(rsi.report_date_added)}</>
          ),
        },
        {
          text: formattedDate(rsi.due_date),
          style: ReportEntryStyle.TEXT,
          customClass: "flex-1 whitespace-pre",
        },
        {
          text: rsi.report_uuid,
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div className="flex gap-1">
              <ClipboardIcon
                onClick={() => {
                  navigator.clipboard.writeText(rsi.report_uuid);
                }}
                className="w-4 cursor-pointer"
              ></ClipboardIcon>
              <span className={"w-16 h-[30px] truncate"}>
                {rsi.report_uuid}
              </span>
            </div>
          ),
        },
        {
          text: `${rsi.client_report_id}`,
          style: ReportEntryStyle.TEXT,
        },
      ],
    };
    if (permissions.canCancelMisconduct) {
      rowItem.data.push({
        text: "Cancel",
        style: ReportEntryStyle.COMPONENT,
        component: <MisconductCancelButton reportUUID={rsi.report_uuid} />,
      });
    }
    return rowItem;
  }

  if (!row) {
    return <LoadingSpinner />;
  } else {
    return <ReportRow reportRow={row} index={index} />;
  }
};

export default ClientQALazyRow;
